import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BRANCHES } from "../data/content";
import { ArrowRight, ArrowLeft, Robot, Sparkle, ShieldStar, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "work_style",
    label: "Which working environment fits you best?",
    options: [
      "Outdoors / physical / hands-on",
      "Inside an office or operations center",
      "Mix of both — fieldwork plus desk time",
      "Aboard ships, aircraft, or vehicles",
    ],
  },
  {
    id: "interests",
    label: "Which areas interest you most?",
    options: [
      "Cyber, computers, and IT",
      "Engineering, mechanics, or aviation",
      "Medicine and healthcare",
      "Intelligence, languages, or analysis",
      "Law enforcement / security",
      "Combat arms / direct-action roles",
    ],
  },
  {
    id: "education_goal",
    label: "What about education after service?",
    options: [
      "I want a college degree paid for via GI Bill",
      "I want a hands-on trade or certification",
      "Both — degree plus certifications",
      "Not a priority for me",
    ],
  },
  {
    id: "term",
    label: "How long do you want to commit?",
    options: [
      "Shortest possible (2-4 years)",
      "Standard term (4-6 years)",
      "I'm open to a longer career (8+ years)",
    ],
  },
  {
    id: "physical",
    label: "How would you describe your fitness right now?",
    options: [
      "Very fit and ready for demanding roles",
      "Average — I can train up",
      "Out of shape but motivated",
    ],
  },
  {
    id: "life",
    label: "What's most important about lifestyle?",
    options: [
      "Travel, deployments, adventure",
      "Stability and predictable schedule",
      "Tech-forward / modern work",
      "Working with people / teaching / leadership",
    ],
  },
];

export default function Quiz() {
  const [step, setStep] = useState(0); // 0 = branches, 1..N = questions, N+1 = result loading/result
  const [branches, setBranches] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const total = QUESTIONS.length + 1; // +1 for branch selection
  const progress = Math.min(100, Math.round(((step) / total) * 100));

  const toggleBranch = (slug) => {
    setBranches((prev) => prev.includes(slug) ? prev.filter((b) => b !== slug) : [...prev, slug]);
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const branchNames = BRANCHES.filter((b) => branches.includes(b.slug)).map((b) => b.name);
      const labeledAnswers = {};
      QUESTIONS.forEach((q) => { labeledAnswers[q.label] = answers[q.id]; });
      const { data } = await axios.post(`${API}/quiz/match`, {
        answers: labeledAnswers,
        branches: branchNames.length ? branchNames : null,
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Quiz failed — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(0); setBranches([]); setAnswers({}); setResult(null); setError("");
  };

  // RESULT VIEW
  if (result) {
    return (
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-20" data-testid="quiz-result">
        <span className="inline-flex items-center gap-2 bg-[#0033A0]/10 text-[#0033A0] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
          <Sparkle size={14} weight="fill" /> Your matches
        </span>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-900 mt-4 leading-[1.05]">
          Top 5 jobs for you.
        </h1>
        {result.summary && (
          <p className="text-slate-700 mt-5 max-w-2xl leading-relaxed">{result.summary}</p>
        )}

        <div className="mt-10 space-y-4">
          {result.matches?.map((m, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 hover:border-[#0033A0] transition-colors" data-testid={`quiz-match-${i}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#0033A0]">
                    <ShieldStar size={14} weight="fill" />
                    {m.branch}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-1">{m.job}</h3>
                  <p className="text-slate-700 mt-2 leading-relaxed text-sm">{m.why}</p>
                </div>
                <div className="text-center bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 min-w-[88px]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Fit</div>
                  <div className="font-display font-black text-2xl text-[#0033A0]">{m.fit_score ?? "—"}</div>
                </div>
              </div>
              {m.branch_slug && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to={`/branches/${m.branch_slug}`} className="inline-flex items-center gap-1 text-sm text-[#0033A0] font-semibold hover:underline">
                    See {m.branch} jobs <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-[#0A1128] text-white rounded-2xl p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FCA5A5]">Want a full report?</p>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-2">Get a Career Deep Dive on these jobs.</h3>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">
              Pick a branch + up to 3 jobs, $1 one-time. Daily duties, training pipeline, certifications, civilian career paths — written for you.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/career-deepdive" data-testid="quiz-to-deepdive" className="inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#B00020] font-semibold py-3 px-6 rounded-md">
              See Deep Dive <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>

        <button onClick={restart} className="mt-8 text-sm text-slate-600 hover:text-[#0033A0] font-semibold" data-testid="quiz-restart">
          ← Take the quiz again
        </button>
      </main>
    );
  }

  // LOADING
  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
        <div className="inline-flex h-16 w-16 rounded-full bg-[#0033A0]/10 items-center justify-center">
          <Robot size={28} weight="fill" className="text-[#0033A0] animate-pulse" />
        </div>
        <h2 className="font-display text-2xl font-bold text-slate-900 mt-6">Boot Buddy is matching you to jobs…</h2>
        <p className="text-slate-600 mt-3">Usually takes 10–20 seconds.</p>
      </main>
    );
  }

  // BRANCH PICKER (step 0)
  if (step === 0) {
    return (
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <QuizHeader progress={progress} step={1} total={total} />
        <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight text-slate-900 mt-6 leading-[1.05]">
          Which branches are you considering?
        </h1>
        <p className="text-slate-700 mt-3">Pick any number, or leave blank to consider all six.</p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
          {BRANCHES.map((b) => {
            const active = branches.includes(b.slug);
            return (
              <button
                key={b.slug}
                onClick={() => toggleBranch(b.slug)}
                data-testid={`quiz-branch-${b.slug}`}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  active ? "border-[#0033A0] bg-[#0033A0]/5" : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-slate-900">{b.name}</span>
                  {active && <CheckCircle size={18} weight="fill" className="text-[#0033A0]" />}
                </div>
                <div className="h-1 w-12 rounded-full mt-2" style={{ background: b.color }} />
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex justify-end">
          <button onClick={() => setStep(1)} data-testid="quiz-next-branches" className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-6 rounded-md transition-colors">
            Continue <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </main>
    );
  }

  // QUESTION STEPS
  const qIndex = step - 1;
  const q = QUESTIONS[qIndex];
  if (!q) {
    // Safety net — if something puts us past the last question without a result, reset.
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
        <h2 className="font-display text-2xl font-bold text-slate-900">Something interrupted the quiz.</h2>
        <p className="text-slate-600 mt-3">{error || "Let's start fresh."}</p>
        <button onClick={restart} data-testid="quiz-recover" className="mt-6 inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-6 rounded-md">
          Restart quiz
        </button>
      </main>
    );
  }
  const isLast = qIndex === QUESTIONS.length - 1;
  const selected = answers[q.id];

  const next = () => {
    if (!selected) return;
    if (isLast) { submit(); return; }
    setStep((s) => s + 1);
  };

  return (
    <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20" data-testid={`quiz-step-${qIndex + 1}`}>
      <QuizHeader progress={progress} step={step + 1} total={total} />
      <h1 className="font-display text-2xl md:text-4xl font-black tracking-tight text-slate-900 mt-6 leading-tight">
        {q.label}
      </h1>

      <div className="mt-8 space-y-3">
        {q.options.map((opt) => {
          const active = selected === opt;
          return (
            <button
              key={opt}
              onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
              data-testid={`quiz-option-${opt.toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 40)}`}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between gap-3 ${
                active ? "border-[#0033A0] bg-[#0033A0]/5" : "border-slate-200 bg-white hover:border-slate-400"
              }`}
            >
              <span className="text-slate-900 font-medium">{opt}</span>
              {active && <CheckCircle size={20} weight="fill" className="text-[#0033A0] shrink-0" />}
            </button>
          );
        })}
      </div>

      {error && <p className="mt-6 text-red-600 text-sm">{error}</p>}

      <div className="mt-10 flex justify-between items-center">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0033A0] font-semibold"
          data-testid="quiz-back"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <button
          onClick={next}
          disabled={!selected}
          data-testid="quiz-next"
          className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-md transition-colors"
        >
          {isLast ? "See my matches" : "Next"} <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </main>
  );
}

function QuizHeader({ progress, step, total }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold uppercase tracking-[0.25em] text-[#0033A0]">Boot Buddy Quiz</span>
        <span className="text-slate-500">Step {step} of {total}</span>
      </div>
      <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-[#0033A0] transition-all" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}
